import { createFileRoute } from "@tanstack/react-router";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  Pagination,
  Spinner,
  Table,
} from "@heroui/react";
import { Icon } from "@iconify/react";

import { useUsers } from "@/features/users/use-users.ts";
import { UserEntity } from "@/features/users/type.ts";
import {
  UsersFilters,
  UsersFiltersValue,
} from "@/features/users/ui/users-filters.tsx";
import {
  UserCityEdit,
  UserNameEdit,
  UserRoleEdit,
} from "@/features/users/ui/user-inline-edits.tsx";
import {
  PAGINATION_ELLIPSIS,
  paginationRange,
} from "@/shared/helper/pagination.ts";

type UsersSearch = {
  page?: number;
  role?: string;
  city?: number;
  phone?: string;
  name?: string;
};

export const Route = createFileRoute("/dashboard/users/")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): UsersSearch => {
    return {
      page: search.page ? Number(search?.page) : undefined,
      role: (search.role as string) || undefined,
      city: search.city ? Number(search.city) : undefined,
      phone: (search.phone as string) || undefined,
      name: (search.name as string) || undefined,
    };
  },
});

function RouteComponent() {
  const { page, role, city, phone, name } = Route.useSearch();
  const navigate = Route.useNavigate();

  const { data, isLoading, isFetching } = useUsers({
    role,
    cityId: city,
    phone,
    name,
    skip: page,
  });

  const totalPages = Math.ceil((data?.total ?? 0) / 12);

  // page в адресной строке отсутствует на первой странице — приводим к числу,
  // иначе на ней не подсвечивается активная кнопка.
  const currentPage = page ?? 1;

  // Любая смена фильтра возвращает на первую страницу: на пятой странице
  // отфильтрованной выборки чаще всего уже пусто.
  const onFiltersChange = (next: UsersFiltersValue) =>
    navigate({ search: { ...next, page: undefined } });

  const openDetails = (userId: string) =>
    navigate({ to: "/dashboard/users/$userId", params: { userId } });

  const renderRow = (user: UserEntity) => (
    <Table.Row id={user.id}>
      <Table.Cell>{user.phoneNumber}</Table.Cell>
      <Table.Cell>
        {user.phoneNumberVerified ? (
          <Icon
            className="size-5 text-success-400"
            icon="ion:checkmark-circle"
          />
        ) : (
          <Icon className="size-5 text-neutral-400" icon="ion:close-circle" />
        )}
      </Table.Cell>
      <Table.Cell>
        <UserNameEdit name={user.name} userId={user.id} />
      </Table.Cell>
      <Table.Cell>
        <UserRoleEdit role={user.role} userId={user.id} />
      </Table.Cell>
      <Table.Cell>
        <UserCityEdit cityId={user.city} userId={user.id} />
      </Table.Cell>
      <Table.Cell>
        <Dropdown>
          <Dropdown.Trigger>
            <Button isIconOnly variant="ghost">
              <Icon
                className="size-5 text-neutral-500"
                icon="ion:ellipsis-horizontal-sharp"
              />
            </Button>
          </Dropdown.Trigger>
          <Dropdown.Popover>
            <DropdownMenu
              onAction={(key) => {
                if (key === "details") openDetails(user.id);
              }}
            >
              <DropdownItem id="details" textValue="details">
                Детали
              </DropdownItem>
              <DropdownItem id="copy" textValue="history">
                История сессии
              </DropdownItem>
              <DropdownItem id="edit" textValue="block">
                Заблокировать
              </DropdownItem>
              <DropdownItem id="delete" textValue="logout">
                Завершить сессию
              </DropdownItem>
            </DropdownMenu>
          </Dropdown.Popover>
        </Dropdown>
      </Table.Cell>
    </Table.Row>
  );

  return (
    <div className="flex flex-col pt-4">
      <div className="flex flex-row gap-2 justify-between h-12 items-center">
        <p className="font-bold text-xl">Пользователи</p>

        {isFetching && !isLoading && <Spinner size="sm" />}
      </div>

      <div className="mt-2">
        <UsersFilters
          value={{ phone, name, role, city }}
          onChange={onFiltersChange}
        />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <Table.Root className="mt-8">
          <Table.Content aria-label="Список пользователей">
            <Table.Header>
              <Table.Column>Телефон</Table.Column>
              <Table.Column>Подтвержден</Table.Column>
              <Table.Column>Имя</Table.Column>
              <Table.Column>Роль</Table.Column>
              <Table.Column>Город</Table.Column>
              <Table.Column> </Table.Column>
            </Table.Header>
            <Table.Body
              items={data?.data ?? []}
              renderEmptyState={() => "Пользователи не найдены"}
            >
              {renderRow}
            </Table.Body>
          </Table.Content>
        </Table.Root>
      )}

      {totalPages > 1 && (
        <div className="mt-4 flex justify-center">
          <Pagination>
            <Pagination.Content>
              <Pagination.Item>
                <Pagination.Previous
                  isDisabled={currentPage <= 1}
                  onPress={() =>
                    navigate({
                      search: (prev) => ({ ...prev, page: currentPage - 1 }),
                    })
                  }
                >
                  Назад
                </Pagination.Previous>
              </Pagination.Item>
              {paginationRange(currentPage, totalPages).map((slot, index) => (
                <Pagination.Item key={`${slot}-${index}`}>
                  {slot === PAGINATION_ELLIPSIS ? (
                    <Pagination.Ellipsis />
                  ) : (
                    <Pagination.Link
                      isActive={currentPage === slot}
                      onPress={() =>
                        navigate({
                          search: (prev) => ({ ...prev, page: slot }),
                        })
                      }
                    >
                      {slot}
                    </Pagination.Link>
                  )}
                </Pagination.Item>
              ))}
              <Pagination.Item>
                <Pagination.Next
                  isDisabled={currentPage >= totalPages}
                  onPress={() =>
                    navigate({
                      search: (prev) => ({ ...prev, page: currentPage + 1 }),
                    })
                  }
                >
                  Вперёд
                </Pagination.Next>
              </Pagination.Item>
            </Pagination.Content>
          </Pagination>
        </div>
      )}
    </div>
  );
}
